import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { reportClientError } from "../../../frontend/lib/error-reporter.ts";
import { ScoreMiniChart } from "../../../shared/components/ScoreMiniChart.tsx";
import { apiRequest } from "../../../shared/lib/api-client.ts";
import { parseNotesField } from "../../../shared/lib/notes-utils.ts";
import { queryKeys } from "../../../shared/lib/query-keys.ts";
import {
  CATEGORY_TOOLTIPS,
  getConfidenceLevel,
  getScoreBreakdown,
  PROVIDER_NAMES,
} from "../../../shared/lib/scoring-utils.ts";
import type { ScoringProgress } from "../../../shared/types/scoring.ts";
import type { EvWorkstationDetail } from "../../ev-workstation/types/index.ts";
import { PeerCohortHeadline } from "../../intelligence/components/PeerCohortHeadline.tsx";
import { PeerCohortPanel } from "../../intelligence/components/PeerCohortPanel.tsx";
import { confirmProjectTge } from "../../leaderboard/api/leaderboard-api.ts";
import type { Project } from "../../leaderboard/types/index.ts";
import { getProjectDetail } from "../api/project-detail-api.ts";
import type {
  AuditEvidence,
  AuditStatusPayload,
  ProjectRankSummary,
  ScoreSnapshot,
} from "../types/index.ts";
import {
  ProjectCallPrepSection,
  ProjectOperatingSummaryArtifact,
  ProjectWorkflowPanel,
} from "./ProjectWorkflowPanel.tsx";

const TGE_SIGNAL_LABELS: Record<string, string> = {
  tokenomics_published: "Tokenomics published",
  airdrop_announced: "Airdrop announced",
  token_launch_announced: "Token launch announced",
  tge_announced: "TGE announced",
  points_ending: "Points season ending",
  airdrop_claim_live: "Airdrop claim live",
  token_distribution: "Token distribution",
};

function formatTgeSignal(signal: string): string {
  // Strip prefix (e.g. "exa:" or "website:")
  const bare = signal.includes(":") ? signal.split(":").slice(1).join(":") : signal;
  return TGE_SIGNAL_LABELS[bare] ?? bare.replace(/_/g, " ");
}

interface TgeSource {
  url: string;
  title: string;
  type: string;
  sourceKind?: string;
  signal: string;
  estimatedDate?: string;
}

const SOURCE_KIND_LABELS: Record<string, string> = {
  own_tweet: "Tweet",
  own_site: "Official",
  trusted_news: "News",
  news: "Web",
};

function TgeSignalsBadge({
  confidence,
  signals,
  sources,
  estimatedDate,
  onConfirmTge,
  confirmed,
  confirmedDate,
}: {
  confidence: number;
  signals: string[];
  sources: TgeSource[];
  estimatedDate?: string | null;
  onConfirmTge?: () => void;
  confirmed?: boolean;
  confirmedDate?: string;
}) {
  const [showPopover, setShowPopover] = useState(false);
  // Guard: tge_sources can arrive as a JSON string from double-encoded JSONB
  const safeSources: TgeSource[] = Array.isArray(sources)
    ? sources
    : typeof sources === "string"
      ? (() => {
          try {
            const p = JSON.parse(sources);
            return Array.isArray(p) ? p : [];
          } catch {
            return [];
          }
        })()
      : [];
  const safeSignals = Array.isArray(signals) ? signals : [];
  const exaSignals = safeSignals.filter((s) => s.startsWith("exa:"));
  const websiteSignals = safeSignals.filter((s) => s.startsWith("website:"));
  const displaySignals = exaSignals.length > 0 ? exaSignals : websiteSignals;
  const topSignals = displaySignals.slice(0, 3).map(formatTgeSignal);

  return (
    <div
      className="tge-signals-section"
      role="tooltip"
      onMouseEnter={() => setShowPopover(true)}
      onMouseLeave={() => setShowPopover(false)}
    >
      <div className="tge-signals-badge">
        <span className="tge-signals-icon">&#9889;</span>
        {confirmed ? (
          <span className="tge-signals-label tge-signals-label--confirmed">
            Launched{confirmedDate ? ` ${confirmedDate}` : ""}
          </span>
        ) : (
          <>
            <span className="tge-signals-label">
              TGE{estimatedDate ? ` ~${estimatedDate}` : ""}
            </span>
            <span className="tge-signals-confidence">{confidence}%</span>
          </>
        )}
        {onConfirmTge && (
          <button
            type="button"
            className="tge-confirm-btn"
            onClick={(e) => {
              e.stopPropagation();
              onConfirmTge();
            }}
          >
            Confirm
          </button>
        )}
      </div>
      {topSignals.length > 0 && (
        <div className="tge-signals-chips">
          {topSignals.map((s) => (
            <span key={s} className="tge-signal-chip">
              {s}
            </span>
          ))}
        </div>
      )}
      {showPopover && (
        <div className="tge-signals-popover">
          <div className="tge-signals-popover-title">TGE Proximity Signals</div>
          <div className="tge-signals-popover-confidence">
            Confidence: {confidence}%
            {estimatedDate && (
              <span className="tge-signals-popover-date">Est. {estimatedDate}</span>
            )}
          </div>
          {safeSources.length > 0
            ? safeSources.map((src, i) => (
                <div key={src.url || i} className="tge-signals-popover-item">
                  <span
                    className={`tge-signals-popover-type tge-signals-popover-type--${src.type}`}
                  >
                    {SOURCE_KIND_LABELS[src.sourceKind ?? ""] ??
                      (/x\.com\/|twitter\.com\//i.test(src.url ?? "")
                        ? "Tweet"
                        : src.type === "confirmed"
                          ? "Official"
                          : "News")}
                  </span>
                  <span className="tge-signals-popover-signal">
                    {formatTgeSignal(`exa:${src.signal}`)}
                  </span>
                  <a
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tge-signals-popover-link"
                    title={src.title}
                  >
                    {src.title || new URL(src.url).hostname}
                  </a>
                </div>
              ))
            : safeSignals.map((signal) => (
                <div key={signal} className="tge-signals-popover-item">
                  <span className="tge-signals-popover-type">
                    {signal.startsWith("exa:") ? "Exa" : "Website"}
                  </span>
                  <span className="tge-signals-popover-signal">{formatTgeSignal(signal)}</span>
                </div>
              ))}
        </div>
      )}
    </div>
  );
}

interface ProjectDetailModalProps {
  project: Project;
  onClose: () => void;
  onOpenRescoreModal: () => void;
  scoringProgress?: ScoringProgress | undefined;
  canManageFounder?: boolean;
  workflowMode?: boolean;
  detailView?: "full" | "call_prep" | "operating_summary";
  onNavigateToWorkstation?: (project: Project, warmContactKey?: string) => void;
  onFocusWarmContact?: (projectId: string, warmContactKey: string) => void;
  /** Opens another project's detail modal (used by Comparables → neighbor click). */
  onOpenProjectDetail?: (project: Project) => void;
}

export function ProjectDetailModal({
  project,
  onClose,
  // Reserved for future rescore button integration
  onOpenRescoreModal: _,
  scoringProgress: __,
  canManageFounder = false,
  workflowMode = false,
  detailView = "full",
  onNavigateToWorkstation,
  onFocusWarmContact,
  onOpenProjectDetail,
}: ProjectDetailModalProps) {
  const queryClient = useQueryClient();
  const [evidence, setEvidence] = useState<AuditEvidence[]>([]);
  const [history, setHistory] = useState<ScoreSnapshot[]>([]);
  const [loadingEvidence, setLoadingEvidence] = useState(true);
  const [evidenceError, setEvidenceError] = useState(false);
  const [auditStatus, setAuditStatus] = useState<AuditStatusPayload | null>(null);
  const [rankSummary, setRankSummary] = useState<ProjectRankSummary | null>(null);
  const [workflowDetail, setWorkflowDetail] = useState<EvWorkstationDetail | null>(null);
  const [founderEdit, setFounderEdit] = useState({ name: "", twitter: "" });
  const [founderSubmitting, setFounderSubmitting] = useState(false);
  const [founderError, setFounderError] = useState<string | null>(null);
  const [showFounderForm, setShowFounderForm] = useState(false);
  const [hoveredWarmFund, setHoveredWarmFund] = useState<string | null>(null);
  const [showConfirmTge, setShowConfirmTge] = useState(false);
  const [tgeDateInput, setTgeDateInput] = useState("");
  const [tgeGeckoId, setTgeGeckoId] = useState("");
  const [tgeNote, setTgeNote] = useState("");
  const [tgeSubmitting, setTgeSubmitting] = useState(false);
  const [tgeError, setTgeError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    setLoadingEvidence(true);
    setEvidenceError(false);
    setWorkflowDetail(null);
    try {
      const detail = await queryClient.fetchQuery({
        queryKey: queryKeys.projectDetail.detail(project.id, canManageFounder ? "admin" : "basic"),
        queryFn: () => getProjectDetail(project.id, canManageFounder || workflowMode),
      });
      setHistory(detail.history);
      setEvidence(detail.evidence);
      setAuditStatus(detail.auditStatus);
      setRankSummary(detail.rankSummary);
      setWorkflowDetail(detail.workflowDetail);
    } catch (err) {
      console.error("Failed to fetch evidence:", err);
      reportClientError({ scope: "ProjectDetailModal:fetchEvidence", error: err });
      setEvidenceError(true);
    } finally {
      setLoadingEvidence(false);
    }
  }, [canManageFounder, project.id, queryClient, workflowMode]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  async function submitFounderUpdate() {
    if (!founderEdit.name.trim() && !founderEdit.twitter.trim()) return;
    setFounderSubmitting(true);
    setFounderError(null);
    try {
      const res = await apiRequest(`/v1/projects/${project.id}/pedigree-rescore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          founderName: founderEdit.name.trim() || undefined,
          founderTwitter: founderEdit.twitter.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        const errObj = errBody.error as Record<string, unknown> | undefined;
        throw new Error(typeof errObj?.message === "string" ? errObj.message : "Rescore failed");
      }
      setShowFounderForm(false);
      setFounderEdit({ name: "", twitter: "" });
      // Refresh modal data and leaderboard
      await fetchDetail();
      void queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard.projectsRoot() });
    } catch (err) {
      setFounderError(err instanceof Error ? err.message : "Could not rescore pedigree");
    } finally {
      setFounderSubmitting(false);
    }
  }

  function parseFuzzyDate(est: string | null | undefined): string {
    if (!est) return "";
    // Try "March 30, 2026" or "March 30 2026"
    const mdy = est.match(/(\w+)\s+(\d{1,2}),?\s+(20\d{2})/);
    if (mdy) {
      const d = new Date(`${mdy[1]} ${mdy[2]}, ${mdy[3]}`);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    // Try "December 29, 2026"
    const my = est.match(/(\w+)\s+(20\d{2})/);
    if (my && !est.match(/Q\d/i)) {
      const d = new Date(`${my[1]} 1, ${my[2]}`);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    return "";
  }

  function extractGeckoId(input: string): string | undefined {
    const trimmed = input.trim();
    if (!trimmed) return undefined;
    // Parse from URL: coingecko.com/en/coins/{id} or coingecko.com/coins/{id}
    const match = trimmed.match(/coingecko\.com\/(?:en\/)?coins\/([a-z0-9-]+)/i);
    if (match?.[1]) return match[1].toLowerCase();
    // If it's just a plain slug (no URL), use as-is
    if (/^[a-z0-9-]+$/i.test(trimmed)) return trimmed.toLowerCase();
    return undefined;
  }

  async function handleConfirmTge() {
    if (!tgeDateInput) return;
    // geckoId became required backend-side as part of the 2026-04-22
    // TGE outcomes audit (tge_outcomes.gecko_id is NOT NULL; manual
    // confirmations must carry a CG identifier so price checkpoints
    // and the rescore pipeline have something to track).
    const geckoId = extractGeckoId(tgeGeckoId);
    if (!geckoId) {
      setTgeError("A CoinGecko URL or slug is required to confirm a TGE.");
      return;
    }
    setTgeSubmitting(true);
    setTgeError(null);
    try {
      await confirmProjectTge(project.id, {
        tgeDate: tgeDateInput,
        geckoId,
        note: tgeNote.trim() || undefined,
      });
      setShowConfirmTge(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard.projectsRoot() });
      onClose();
    } catch (err) {
      setTgeError(err instanceof Error ? err.message : "Failed to confirm TGE");
    } finally {
      setTgeSubmitting(false);
    }
  }

  function handleOverlayKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      onClose();
    }
  }

  const scoreFreshness = useMemo(() => {
    if (!project.last_audited_at) return null;
    const scoredAt = new Date(project.last_audited_at);
    const days = Math.floor((Date.now() - scoredAt.getTime()) / 86400000);
    const dateStr = scoredAt.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const run = auditStatus?.latestRun;
    const triggerLabels: Record<string, string> = {
      api_submit: "Submitted via UI",
      api_ingest: "Ingested via API",
      discovery: "Discovered automatically",
      auto_rescore: "Auto-rescored (stale)",
      manual: "Manually triggered",
      unknown: "Scored",
    };
    const flagLabels: Record<string, string> = {
      stale_rescore: "periodic rescore",
      new_raise: "new fundraising detected",
      rescore_raises_fix: "raises data fix",
      deferred_catchup: "deferred scoring catchup",
    };

    const triggerLabel = triggerLabels[run?.triggerSource ?? ""] ?? "Scored";
    const flagDetail = run?.triggerFlags?.map((f) => flagLabels[f] ?? f).join(", ");
    const actionLine = flagDetail ? `${triggerLabel} (${flagDetail})` : triggerLabel;

    const lines = [`${actionLine} on ${dateStr}`];

    if (days <= 7) {
      lines.push("Score data is current.");
      return { label: `${days}d ago`, tone: "fresh" as const, tooltip: lines.join("\n") };
    }
    if (days <= 30) {
      lines.push("Social and momentum signals may have shifted.");
      return { label: `${days}d ago`, tone: "aging" as const, tooltip: lines.join("\n") };
    }
    lines.push("Score is stale. Rescore recommended.");
    return { label: `${days}d ago`, tone: "stale" as const, tooltip: lines.join("\n") };
  }, [project.last_audited_at, auditStatus?.latestRun]);

  const parsed = parseNotesField(project.notes);
  const founder = auditStatus?.founder;
  const backers = auditStatus?.backers;
  const hasStructuredFounder =
    founder &&
    (founder.founderName || founder.founderTwitter) &&
    (founder.confidence == null || founder.confidence > 0);
  const hasStructuredBackers = !!backers;
  const warmIntros = auditStatus?.warmIntros;
  const normalizeFund = (n: string) => n.toLowerCase().replace(/[^a-z0-9]/g, "");
  const warmFundSet = new Set((warmIntros?.matchedFundNames ?? []).map(normalizeFund));

  const evidenceFailed = evidence.filter((e) => e.error_message);
  const notesFailed = parsed.failedProviders;
  const hasFailures = evidenceFailed.length > 0 || notesFailed.length > 0;

  const pedigreeEvidence = evidence.find((e) => e.provider_slug === "pedigree" && e.raw_payload);
  const pedigreeAnalysis = pedigreeEvidence
    ? ((pedigreeEvidence.raw_payload as Record<string, unknown>)?.analysis as
        | Record<string, unknown>
        | undefined)
    : null;
  const evidenceFounders = (
    pedigreeAnalysis?.founders as
      | {
          name?: string;
          twitter?: string;
          tier?: number;
          tierReason?: string;
          confidence?: number;
        }[]
      | undefined
  )?.filter((f) => f.confidence == null || f.confidence > 0 || f.twitter);

  const raisesEvidence = evidence.find((e) => e.provider_slug === "raises" && e.raw_payload);
  const raisesPayload = raisesEvidence?.raw_payload as Record<string, unknown> | undefined;
  const raisesAnalysis = raisesPayload?.analysis as Record<string, unknown> | undefined;
  const raises = (raisesAnalysis?.raises || raisesPayload?.raises) as
    | {
        amount?: string;
        round?: string;
        investors?: string[];
        leadInvestor?: string;
      }[]
    | undefined;
  const evidenceInvestors = raises
    ? [...new Set(raises.flatMap((r) => r.investors || []).filter(Boolean))]
    : [];

  const breakdownSource = !loadingEvidence && evidence.length > 0 ? evidence : project;
  const breakdown = getScoreBreakdown(breakdownSource);
  const percentileTier = rankSummary?.percentileTier ?? null;
  const categoryRanks:
    | (ProjectRankSummary["categoryRanks"] & { overall: number | null; total: number })
    | null = rankSummary
    ? {
        ...rankSummary.categoryRanks,
        overall: rankSummary.overallRank,
        total: rankSummary.totalProjects,
      }
    : null;
  const categoryBreakdown = [
    { label: "Social", pts: Number(breakdown.social), max: 20 },
    { label: "Backers", pts: Number(breakdown.backers), max: 25 },
    { label: "Pedigree", pts: Number(breakdown.pedigree), max: 20 },
    { label: "Momentum", pts: Number(breakdown.momentum), max: 25 },
    { label: "Dev", pts: Number(breakdown.dev), max: 5 },
    { label: "Revenue", pts: Number(breakdown.revenue), max: 5 },
  ].sort((a, b) => b.pts - a.pts);
  const callPrepOnly = workflowMode && detailView === "call_prep";
  const operatingSummaryOnly = workflowMode && detailView === "operating_summary";

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      onKeyDown={handleOverlayKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="modal project-detail-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="modal-header">
          <div className="modal-header-left">
            <div className="modal-title-row">
              <h2 id="modal-title">{project.name ?? project.twitter_handle}</h2>
              <a
                href={`https://twitter.com/${project.twitter_handle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="modal-handle"
              >
                @{project.twitter_handle}
              </a>
              {project.category_name && (
                <span
                  className="category-badge"
                  style={{ backgroundColor: project.category_color ?? "#6b7280" }}
                >
                  {project.category_name}
                </span>
              )}
              {project.has_tge_hints && (project.tge_confidence ?? 0) >= 50 && (
                <TgeSignalsBadge
                  confidence={Number(project.tge_confidence)}
                  signals={project.tge_signals ?? []}
                  sources={(project.tge_sources ?? []) as TgeSource[]}
                  estimatedDate={project.tge_estimated_date}
                  onConfirmTge={
                    (project.tge_confidence ?? 0) >= 90 &&
                    !project.is_archived &&
                    !project.tge_confirmed_at &&
                    canManageFounder
                      ? () => {
                          setTgeDateInput(parseFuzzyDate(project.tge_estimated_date));
                          setShowConfirmTge(true);
                        }
                      : undefined
                  }
                  confirmed={!!project.tge_confirmed_at}
                  confirmedDate={project.tge_confirmed_date ?? undefined}
                />
              )}
              {scoreFreshness && (
                <span
                  className={`score-freshness score-freshness--${scoreFreshness.tone}`}
                  data-tooltip={scoreFreshness.tooltip}
                >
                  {scoreFreshness.label}
                </span>
              )}
            </div>
            {project.description && <p className="modal-description">{project.description}</p>}
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close modal">
            &times;
          </button>
        </div>
        <div className="modal-body">
          {callPrepOnly ? (
            <>
              {evidenceError && (
                <div className="evidence-error">
                  <span className="evidence-error-text">Failed to load call prep</span>
                  <button type="button" className="btn" onClick={fetchDetail}>
                    Retry
                  </button>
                </div>
              )}

              {workflowDetail ? (
                <ProjectCallPrepSection detail={workflowDetail} onRefresh={fetchDetail} />
              ) : null}

              {!workflowDetail && !loadingEvidence && !evidenceError ? (
                <div className="community-empty">Call prep is unavailable for this project.</div>
              ) : null}

              {loadingEvidence && (
                <div className="loading">
                  <div className="spinner" />
                  <span className="loading-text">Loading call prep</span>
                </div>
              )}
            </>
          ) : operatingSummaryOnly ? (
            <>
              {evidenceError && (
                <div className="evidence-error">
                  <span className="evidence-error-text">Failed to load operating summary</span>
                  <button type="button" className="btn" onClick={fetchDetail}>
                    Retry
                  </button>
                </div>
              )}

              {workflowDetail ? (
                <div className="project-workflow-panel">
                  <div className="project-workflow-panel__header">
                    <span className="section-title">Operating Summary</span>
                  </div>
                  <div className="project-workflow-panel__artifacts project-workflow-panel__artifacts--single">
                    <ProjectOperatingSummaryArtifact detail={workflowDetail} />
                  </div>
                </div>
              ) : null}

              {!workflowDetail && !loadingEvidence && !evidenceError ? (
                <div className="community-empty">
                  Operating summary is unavailable for this project.
                </div>
              ) : null}

              {loadingEvidence && (
                <div className="loading">
                  <div className="spinner" />
                  <span className="loading-text">Loading operating summary</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="total-score">
                <div>
                  <div className="score-main">
                    <div className="total-score-value">
                      {project.current_alpha_score != null
                        ? Number(project.current_alpha_score).toFixed(1)
                        : "—"}
                    </div>
                    <div className="total-score-max">/ 100</div>
                  </div>
                  <div className="total-score-label">
                    Alpha Score
                    {categoryRanks != null && (categoryRanks.overall ?? 0) > 0 && (
                      <span className="overall-rank">
                        #{categoryRanks.overall} of {categoryRanks.total}
                      </span>
                    )}
                    {percentileTier && <span className="percentile-badge">{percentileTier}</span>}
                  </div>
                </div>
                {history.length > 1 && <ScoreMiniChart historyData={history} />}
                <div className="confidence-display">
                  <div
                    className={`confidence-value ${project.confidence_level != null ? getConfidenceLevel(Number(project.confidence_level)).className : ""}`}
                  >
                    {project.confidence_level != null
                      ? `${(Number(project.confidence_level) * 100).toFixed(0)}%`
                      : "—"}
                  </div>
                  <div className="confidence-label">
                    Confidence
                    {project.confidence_level != null && (
                      <span className="confidence-tier-label">
                        {getConfidenceLevel(Number(project.confidence_level)).label}
                      </span>
                    )}
                  </div>
                  {project.rank_change != null && Number(project.rank_change) !== 0 && (
                    <span
                      className={`rank-badge ${Number(project.rank_change) > 0 ? "rank-up" : "rank-down"}`}
                    >
                      <span className="rank-arrow">
                        {Number(project.rank_change) > 0 ? "▲" : "▼"}
                      </span>
                      {Math.abs(Number(project.rank_change))}
                    </span>
                  )}
                </div>
              </div>

              {!workflowMode && <PeerCohortHeadline projectId={project.id} />}

              {evidenceError && (
                <div className="evidence-error">
                  <span className="evidence-error-text">Failed to load evidence data</span>
                  <button type="button" className="btn" onClick={fetchDetail}>
                    Retry
                  </button>
                </div>
              )}

              {!workflowMode && (
                <table className="score-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Score</th>
                      <th>Max</th>
                      <th>Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryBreakdown.map((cat) => {
                      const rankKey = cat.label.toLowerCase();
                      const rank =
                        categoryRanks?.[rankKey as keyof ProjectRankSummary["categoryRanks"]] ??
                        null;
                      const showRank = rank != null && rank > 0 && rank <= 10;
                      return (
                        <tr key={cat.label} title={CATEGORY_TOOLTIPS[cat.label]}>
                          <td>{cat.label}</td>
                          <td className="mono">{cat.pts.toFixed(1)}</td>
                          <td className="mono dim">/ {cat.max}</td>
                          <td className="mono rank-cell">{showRank ? `#${rank}` : ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {!workflowMode && hasFailures && (
                <div className="failed-details-section">
                  <div className="section-label">Failed Providers</div>
                  {evidenceFailed.map((e) => (
                    <div key={e.id} className="failed-detail-row">
                      <span className="failed-name">
                        {PROVIDER_NAMES[e.provider_slug] ?? e.provider_slug}
                      </span>
                      <span className="failed-reason">{e.error_message}</span>
                    </div>
                  ))}
                  {evidenceFailed.length === 0 &&
                    notesFailed.map((slug) => (
                      <div key={slug} className="failed-detail-row">
                        <span className="failed-name">{PROVIDER_NAMES[slug] ?? slug}</span>
                      </div>
                    ))}
                </div>
              )}

              {hasStructuredFounder ? (
                <div className="founder-section">
                  <div className="section-label">
                    Founder / Pedigree
                    {canManageFounder && !showFounderForm && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ marginLeft: 8, fontSize: "0.7rem", padding: "2px 8px" }}
                        onClick={() => {
                          setFounderEdit({
                            name: founder.founderName ?? "",
                            twitter: founder.founderTwitter ?? "",
                          });
                          setShowFounderForm(true);
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {!showFounderForm && (
                    <>
                      <div className="founder-info">
                        {(founder.founderName || founder.founderTwitter) && (
                          <div className="founder-name">
                            {founder.founderName ?? `@${founder.founderTwitter}`}
                            {founder.founderName && founder.founderTwitter && (
                              <a
                                href={`https://twitter.com/${founder.founderTwitter}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="founder-handle"
                              >
                                {" "}
                                @{founder.founderTwitter}
                              </a>
                            )}
                            {!founder.founderName && founder.founderTwitter && (
                              <a
                                href={`https://twitter.com/${founder.founderTwitter}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="founder-handle"
                                style={{ marginLeft: 0 }}
                              >
                                {" "}
                              </a>
                            )}
                          </div>
                        )}
                        {founder.founderTier != null && (
                          <span className={`tier-badge tier-${founder.founderTier}`}>
                            Tier {founder.founderTier}
                          </span>
                        )}
                      </div>
                      {founder.founderSocial && (
                        <div className="founder-social-scores">
                          {founder.founderSocial.twitterScore != null && (
                            <span className="founder-social-item">
                              TwitterScore: {Math.round(founder.founderSocial.twitterScore)}
                            </span>
                          )}
                          {founder.founderSocial.moniScore != null && (
                            <span className="founder-social-item">
                              Moni: {Math.round(founder.founderSocial.moniScore).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                      {founder.tierReason && (
                        <div className="founder-reason">{founder.tierReason}</div>
                      )}
                    </>
                  )}
                </div>
              ) : evidenceFounders && evidenceFounders.length > 0 ? (
                <div className="founder-section">
                  <div className="section-label">
                    Founder / Pedigree
                    {canManageFounder && !showFounderForm && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ marginLeft: 8, fontSize: "0.7rem", padding: "2px 8px" }}
                        onClick={() => setShowFounderForm(true)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {!showFounderForm &&
                    evidenceFounders.map((f, i) => (
                      <div key={f.twitter || i} className="founder-info">
                        <span className="founder-name">
                          {f.name || "Unknown"}
                          {f.twitter && (
                            <a
                              href={`https://twitter.com/${f.twitter}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="founder-handle"
                            >
                              {" "}
                              @{f.twitter}
                            </a>
                          )}
                        </span>
                        {f.tier && (
                          <span className={`tier-badge tier-${f.tier}`}>Tier {f.tier}</span>
                        )}
                        {f.tierReason && <div className="founder-reason">{f.tierReason}</div>}
                      </div>
                    ))}
                </div>
              ) : parsed.founder ? (
                <div className="founder-section">
                  <div className="section-label">
                    Founder / Pedigree
                    {canManageFounder && !showFounderForm && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ marginLeft: 8, fontSize: "0.7rem", padding: "2px 8px" }}
                        onClick={() => setShowFounderForm(true)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {!showFounderForm && (
                    <>
                      <div className="founder-info">
                        <div className="founder-name">
                          {parsed.founder.name}
                          {parsed.founder.twitter && (
                            <a
                              href={`https://twitter.com/${parsed.founder.twitter}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="founder-handle"
                            >
                              {" "}
                              @{parsed.founder.twitter}
                            </a>
                          )}
                        </div>
                        {parsed.founder.tier != null && (
                          <span className={`tier-badge tier-${parsed.founder.tier}`}>
                            Tier {parsed.founder.tier}
                          </span>
                        )}
                      </div>
                      {parsed.founder.tierReason && (
                        <div className="founder-reason">{parsed.founder.tierReason}</div>
                      )}
                      {parsed.founder.bullets.length > 0 && (
                        <ul className="founder-bullets">
                          {parsed.founder.bullets.map((b) => (
                            <li key={`${parsed.founder?.name ?? "founder"}-${b}`}>{b}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="founder-section">
                  <div className="section-label">Founder / Pedigree</div>
                  {!showFounderForm && !loadingEvidence && (
                    <div className="founder-info">
                      <span className="founder-name dim">No founder detected</span>
                      {canManageFounder && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ marginLeft: 8, fontSize: "0.7rem", padding: "2px 8px" }}
                          onClick={() => setShowFounderForm(true)}
                        >
                          Add Founder
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {canManageFounder && showFounderForm && (
                <div className="founder-edit-form">
                  <div className="founder-edit-field">
                    <label className="founder-edit-label" htmlFor="founder-name-input">
                      Founder Name
                    </label>
                    <input
                      id="founder-name-input"
                      type="text"
                      placeholder="e.g. Vitalik Buterin"
                      value={founderEdit.name}
                      onChange={(e) =>
                        setFounderEdit((prev) => ({ ...prev, name: e.target.value }))
                      }
                      disabled={founderSubmitting}
                    />
                  </div>
                  <div className="founder-edit-field">
                    <label className="founder-edit-label" htmlFor="founder-twitter-input">
                      Twitter Handle <span className="founder-edit-at">@</span>
                    </label>
                    <div className="founder-edit-handle-wrap">
                      <span className="founder-edit-handle-prefix">@</span>
                      <input
                        id="founder-twitter-input"
                        type="text"
                        placeholder="e.g. VitalikButerin"
                        value={founderEdit.twitter}
                        onChange={(e) =>
                          setFounderEdit((prev) => ({
                            ...prev,
                            twitter: e.target.value.replace(/^@/, ""),
                          }))
                        }
                        disabled={founderSubmitting}
                      />
                    </div>
                  </div>
                  {founderError && <div className="founder-edit-error">{founderError}</div>}
                  <div className="founder-edit-actions">
                    <button
                      type="button"
                      className="btn"
                      onClick={submitFounderUpdate}
                      disabled={
                        founderSubmitting ||
                        (!founderEdit.name.trim() && !founderEdit.twitter.trim())
                      }
                    >
                      {founderSubmitting ? "Rescoring..." : "Rescore Pedigree"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowFounderForm(false);
                        setFounderError(null);
                      }}
                      disabled={founderSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {hasStructuredBackers ? (
                <div className="backers-section">
                  <div className="section-label">
                    Backers / Investors
                    {warmIntros?.hasMatches && <span className="warm-intro-badge">Warm Leads</span>}
                  </div>
                  <div className="backers-meta">
                    {backers.totalFunding != null && (
                      <span className="meta-chip">
                        $
                        {backers.totalFunding >= 1_000_000
                          ? `${Math.round(backers.totalFunding / 1_000_000)}M`
                          : backers.totalFunding >= 1_000
                            ? `${Math.round(backers.totalFunding / 1_000)}K`
                            : backers.totalFunding}{" "}
                        raised
                      </span>
                    )}
                    {backers.latestValuation != null && backers.latestValuation > 0 && (
                      <span className="meta-chip meta-chip--valuation">
                        $
                        {backers.latestValuation >= 1_000_000_000
                          ? `${(backers.latestValuation / 1_000_000_000).toFixed(1)}B`
                          : backers.latestValuation >= 1_000_000
                            ? `${Math.round(backers.latestValuation / 1_000_000)}M`
                            : `${Math.round(backers.latestValuation / 1_000)}K`}{" "}
                        valuation
                      </span>
                    )}
                    {backers.latestStage && (
                      <span className="meta-chip">{backers.latestStage}</span>
                    )}
                    {backers.roundCount != null && backers.roundCount > 0 && (
                      <span className="meta-chip">
                        {backers.roundCount} round{backers.roundCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {backers.tierCounts && (
                    <div className="investor-tiers">
                      {backers.tierCounts.tier1Names.length > 0 && (
                        <div className="tier-row">
                          <span className="tier-badge tier-1">T1</span>
                          <span>{backers.tierCounts.tier1Names.join(", ")}</span>
                        </div>
                      )}
                      {backers.tierCounts.tier2Names.length > 0 && (
                        <div className="tier-row">
                          <span className="tier-badge tier-2">T2</span>
                          <span>{backers.tierCounts.tier2Names.join(", ")}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {backers.allInvestors.length > 0 && (
                    <div className="all-investors">
                      {backers.allInvestors.map((name) => {
                        const normalizedName = normalizeFund(name);
                        const isWarmInvestor = warmFundSet.has(normalizedName);
                        if (!isWarmInvestor) {
                          return (
                            <span key={name} className="investor-chip">
                              {name}
                            </span>
                          );
                        }

                        return (
                          <button
                            key={name}
                            className="investor-chip investor-chip--warm"
                            title="Warm intro available"
                            type="button"
                            onMouseEnter={() => setHoveredWarmFund(normalizedName)}
                            onMouseLeave={() => setHoveredWarmFund(null)}
                            onFocus={() => setHoveredWarmFund(normalizedName)}
                            onBlur={() => setHoveredWarmFund(null)}
                          >
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {warmIntros?.hasMatches && warmIntros.matches.length > 0 && (
                    <div className="warm-intro-contacts">
                      <div className="warm-intro-contacts-label">Known Contacts</div>
                      {[...warmIntros.matches]
                        .sort(
                          (a, b) =>
                            Number(a.contact.isTelegramGroup) - Number(b.contact.isTelegramGroup)
                        )
                        .map((m) => {
                          const isHighlighted = hoveredWarmFund === normalizeFund(m.fundName);
                          const handleWarmIntroContactSelect =
                            workflowMode && onFocusWarmContact
                              ? () => {
                                  onFocusWarmContact(
                                    project.id,
                                    `${m.fundName}::${m.contact.name}`
                                  );
                                  onClose();
                                }
                              : !workflowMode && onNavigateToWorkstation
                                ? () =>
                                    onNavigateToWorkstation(
                                      project,
                                      `${m.fundName}::${m.contact.name}`
                                    )
                                : undefined;
                          const rowClassName = `warm-intro-contact-row${m.contact.isTelegramGroup ? " warm-intro-contact-row--tg" : ""}${isHighlighted ? " warm-intro-contact-row--highlighted" : ""}${handleWarmIntroContactSelect ? " warm-intro-contact-row--clickable" : ""}`;
                          const rowContent = (
                            <>
                              {m.contact.isTelegramGroup ? (
                                <>
                                  <span className="warm-intro-contact-tg-label">TG</span>
                                  <span className="warm-intro-contact-name">{m.contact.name}</span>
                                </>
                              ) : (
                                <>
                                  <span className="warm-intro-contact-name">{m.contact.name}</span>
                                  {m.contact.role && (
                                    <span className="warm-intro-contact-role">
                                      · {m.contact.role}
                                    </span>
                                  )}
                                </>
                              )}
                              <span className="warm-intro-contact-fund">@ {m.fundName}</span>
                              {(m.contact.directContactOwners?.length ?? 0) > 0 && (
                                <span className="warm-intro-contact-owners">
                                  {m.contact.directContactOwners?.map((owner) => (
                                    <span
                                      key={owner}
                                      className={`warm-intro-owner-pill${owner === "Ev" ? " warm-intro-owner-pill--ev" : ""}`}
                                    >
                                      {owner}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </>
                          );

                          if (!handleWarmIntroContactSelect) {
                            return (
                              <div key={`${m.fundName}-${m.contact.name}`} className={rowClassName}>
                                {rowContent}
                              </div>
                            );
                          }

                          return (
                            <button
                              key={`${m.fundName}-${m.contact.name}`}
                              className={rowClassName}
                              type="button"
                              onClick={handleWarmIntroContactSelect}
                            >
                              {rowContent}
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              ) : evidenceInvestors.length > 0 ? (
                <div className="backers-section">
                  <div className="section-label">Backers / Investors</div>
                  <div className="all-investors">
                    {evidenceInvestors.map((name) => (
                      <span
                        key={name}
                        className={`investor-chip${warmFundSet.has(normalizeFund(name)) ? " investor-chip--warm" : ""}`}
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              ) : parsed.backerText ? (
                <div className="backers-section">
                  <div className="section-label">Backers / Investors</div>
                  <div className="backers-meta">
                    <span className="meta-chip">{parsed.backerText}</span>
                  </div>
                </div>
              ) : null}

              {!workflowMode && (
                <PeerCohortPanel projectId={project.id} onOpenProjectDetail={onOpenProjectDetail} />
              )}

              {!workflowMode && parsed.remainingNotes.length > 0 && (
                <div className="notes-section">
                  <div className="notes-label">Notes</div>
                  <div className="notes-content">{parsed.remainingNotes.join(" | ")}</div>
                </div>
              )}

              {workflowMode && workflowDetail && (
                <ProjectWorkflowPanel detail={workflowDetail} onRefresh={fetchDetail} />
              )}

              {!workflowMode && canManageFounder && onNavigateToWorkstation && (
                <div className="workstation-link-section">
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    onClick={() => onNavigateToWorkstation(project)}
                  >
                    View in Ev's Workstation
                  </button>
                </div>
              )}

              {loadingEvidence && (
                <div className="loading">
                  <div className="spinner" />
                  <span className="loading-text">Loading evidence</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showConfirmTge && (
        // biome-ignore lint/a11y/noStaticElementInteractions: modal backdrop
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setShowConfirmTge(false)}
          onKeyDown={(e) => e.key === "Escape" && setShowConfirmTge(false)}
        >
          <div
            className="review-modal"
            role="dialog"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h3>Confirm TGE — {project.name || project.twitter_handle}</h3>
            <label className="review-modal__field">
              <span>TGE Date (exact)</span>
              <input
                type="date"
                value={tgeDateInput}
                onChange={(e) => setTgeDateInput(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                required
              />
            </label>
            <label className="review-modal__field">
              <span>
                CoinGecko URL <strong>(required)</strong>
              </span>
              <input
                type="text"
                value={tgeGeckoId}
                onChange={(e) => setTgeGeckoId(e.target.value)}
                placeholder="https://www.coingecko.com/en/coins/edgex"
                required
              />
            </label>
            <label className="review-modal__field">
              <span>Note (optional)</span>
              <input
                type="text"
                value={tgeNote}
                onChange={(e) => setTgeNote(e.target.value)}
                placeholder="Additional context..."
                maxLength={500}
              />
            </label>
            {tgeError && (
              <div className="review-error" style={{ marginBottom: "0.5rem" }}>
                {tgeError}
              </div>
            )}
            <div className="review-modal__actions">
              <button
                type="button"
                className="btn btn-small btn-secondary"
                onClick={() => setShowConfirmTge(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-small btn-primary"
                disabled={!tgeDateInput || !tgeGeckoId.trim() || tgeSubmitting}
                onClick={handleConfirmTge}
              >
                {tgeSubmitting ? "Confirming..." : "Confirm TGE"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
